import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  catchError,
  filter,
  switchMap,
  take,
  throwError,
} from 'rxjs';
import { AuthService } from './auth.service';
import { AuthApiService } from './auth-api.service';

let isRefreshing = false;
const refreshSubject = new BehaviorSubject<string | null>(null);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // /auth/login must stay excluded: wrong credentials are a 401 (INVALID_CREDENTIALS)
  // since the global-exception-handler contract, not a session problem to refresh.
  if (req.url.includes('/auth/login') || req.url.includes('/auth/token/refresh')) {
    return next(req);
  }

  const authService = inject(AuthService);
  const authApiService = inject(AuthApiService);

  return next(req).pipe(
    catchError((err) => {
      if (err instanceof HttpErrorResponse && err.status === 401) {
        return handle401(req, next, authService, authApiService);
      }
      return throwError(() => err);
    }),
  );
};

function handle401(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
  authApiService: AuthApiService,
): Observable<HttpEvent<unknown>> {
  const refresh = authService.getRefreshToken();
  if (!refresh || authService.isRefreshTokenExpired()) {
    authService.logout();
    return throwError(() => new Error('Session expired'));
  }

  if (!isRefreshing) {
    isRefreshing = true;
    refreshSubject.next(null);

    return authApiService.refreshAccessToken(refresh).pipe(
      switchMap((res) => {
        isRefreshing = false;
        authService.saveAccessToken(res.access);
        refreshSubject.next(res.access);
        return next(retryWithToken(req, res.access));
      }),
      catchError((err) => {
        isRefreshing = false;
        authService.logout();
        return throwError(() => err);
      }),
    );
  }

  return refreshSubject.pipe(
    filter((token): token is string => token !== null),
    take(1),
    switchMap((token) => next(retryWithToken(req, token))),
  );
}

function retryWithToken(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return req.clone({
    setHeaders: { Authorization: `Bearer ${token}` },
  });
}
